import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search, Loader2, Send, CalendarDays, BadgeCheck, HardHat, FileText, X, Mail, Download, AlertTriangle } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { StatusTrackingTable, StatusTone } from '../ui/StatusTrackingTable';
import { StatusFilterChips } from '../ui/StatusFilterChips';
import { sameEmailBatch, batchActionLabel } from './extrasEmailBatch';
import { GroupedRequest, groupByPartnerAndEvent, postesIncomplets } from './extrasGrouping';
import { EmailHistoryModal } from '../ui/EmailHistoryModal';
import { useAuth } from '../../context/AuthContext';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { generateExtrasConfirmedPdf } from '../../lib/extrasConfirmedPdfGenerator';

interface RequestEntry {
    request_id: string;
    extra_type_id: string;
    type_label: string;
    desired_people: string[];
    confirmed_comment: string;
    event_id: string | null;
    event_label: string;
    quantity: number;
    request_date: string;
    start_time: string;
    end_time: string;
    status: 'a_demander' | 'demandee';
    requested_at: string | null;
    confirm_token: string;
    confirmed_at: string | null;
    document_file_name: string;
    document_mime_type: string;
    email_count: number;
    created_at: string;
    /** Renseigné quand l'horaire a changé APRÈS la confirmation du partenaire : start_time/end_time portent
     *  déjà le nouvel horaire, mais le partenaire a confirmé l'ancien et doit être reprévenu. */
    hours_changed_at?: string | null;
    /** Besoin total enregistre sur la privatisation pour ce poste, et ce qui est reellement pourvu
     *  (demandes partenaire + salaries internes bookes) - alimente le repere "2 sur 4 demandes". */
    need_total?: number;
    covered_total?: number;
}

type StatusValue = 'a_demander' | 'demandee' | 'confirmee';

const STATUS_TONE: Record<StatusValue, StatusTone> = {
    a_demander: { label: 'À demander', className: 'bg-surface-soft text-content-muted' },
    demandee: { label: 'Demandée', className: 'bg-green-500/10 text-green-500' },
    confirmee: { label: 'Confirmée', className: 'bg-brand-500/10 text-brand-600 dark:text-brand-400' },
};

// Du moins avancé au plus avancé : le statut d'un groupe est celui de sa ligne la moins avancée,
// c'est ce qu'il reste à faire qui doit sauter aux yeux.
const STATUS_ORDER: StatusValue[] = ['a_demander', 'demandee', 'confirmee'];
const deriveStatus = (r: RequestEntry): StatusValue => (r.confirmed_at ? 'confirmee' : r.status === 'demandee' ? 'demandee' : 'a_demander');

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};
const fmtDateTime = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
};

const RequestDetailModal: React.FC<{ entry: RequestEntry; actions?: React.ReactNode; onClose: () => void }> = ({ entry, actions, onClose }) => {
    const [docUrl, setDocUrl] = React.useState<string | null>(null);
    const isImage = (entry.document_mime_type || '').startsWith('image/');

    React.useEffect(() => {
        if (!entry.document_file_name) return;
        const token = localStorage.getItem('token');
        fetch(`/api/extras-requests/${entry.request_id}/document`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => (res.ok ? res.blob() : Promise.reject()))
            .then((blob) => setDocUrl(URL.createObjectURL(blob)))
            .catch(() => setDocUrl(null));
    }, [entry.request_id, entry.document_file_name]);

    return (
        <Modal open onClose={onClose} title={entry.type_label} icon={<HardHat size={16} />} maxWidth="max-w-md"
            footer={<>{actions}<Button variant="secondary" onClick={onClose}>Fermer</Button></>}>
            <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                    <span className="text-content-muted">Type</span>
                    <span className="text-content font-bold">{entry.type_label}</span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-content-muted">Quantité</span>
                    <span className="text-content font-bold">{entry.quantity}</span>
                </div>
                {entry.desired_people?.length > 0 && (
                    <div className="flex items-start justify-between gap-2">
                        <span className="text-content-muted shrink-0">Personnes recommandées</span>
                        <span className="text-content font-bold text-right">{entry.desired_people.join(', ')}</span>
                    </div>
                )}
                {entry.event_label && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted">Privatisation</span>
                        <span className="text-content font-bold">{entry.event_label}</span>
                    </div>
                )}
                {entry.request_date && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted flex items-center gap-1.5"><CalendarDays size={13} /> Date</span>
                        <span className="text-content">{fmtDate(entry.request_date)} {entry.start_time ? `à ${entry.start_time}` : ''}{entry.end_time ? ` - ${entry.end_time}` : ''}</span>
                    </div>
                )}
                {/* L'horaire du type a été modifié dans la privatisation APRÈS que le partenaire ait
                    confirmé : la demande garde volontairement l'horaire qu'il a accepté, mais l'écart doit
                    sauter aux yeux — sinon l'établissement croit avoir corrigé un horaire que le partenaire
                    n'a jamais reçu. Bug constaté par Emeni le 2026-08-24. */}
                {entry.hours_changed_at && (
                    <div className="flex items-start gap-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                        <span>
                            Horaire modifié après la confirmation du partenaire. L'horaire ci-dessus est le bon,
                            mais il a confirmé l'ancien : renvoyez-lui la demande pour qu'il reconfirme.
                        </span>
                    </div>
                )}
                {entry.requested_at && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted flex items-center gap-1.5"><Send size={13} /> Demandée le</span>
                        <span className="text-content">{fmtDateTime(entry.requested_at)}</span>
                    </div>
                )}
                {entry.confirmed_at && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted flex items-center gap-1.5"><BadgeCheck size={13} /> Confirmée le</span>
                        <span className="text-content">{fmtDateTime(entry.confirmed_at)}</span>
                    </div>
                )}
                {entry.confirmed_comment && (
                    <div className="space-y-1">
                        <span className="text-content-muted block">Commentaire du prestataire</span>
                        <p className="text-content bg-surface-soft border border-border rounded-lg px-3 py-2 whitespace-pre-wrap">{entry.confirmed_comment}</p>
                    </div>
                )}
                {entry.document_file_name && (
                    <div className="space-y-1.5">
                        <span className="text-content-muted flex items-center gap-1.5"><FileText size={13} /> Document joint</span>
                        {isImage && docUrl && (
                            <a href={docUrl} target="_blank" rel="noreferrer">
                                <img src={docUrl} alt={entry.document_file_name} className="max-h-40 rounded-lg border border-border object-contain" />
                            </a>
                        )}
                        {docUrl ? (
                            <a href={docUrl} download={entry.document_file_name} className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                <FileText size={13} /> {entry.document_file_name} — Télécharger
                            </a>
                        ) : (
                            <span className="text-content truncate block max-w-[280px]">{entry.document_file_name}</span>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export const ExtrasPartnerRequestsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const { user } = useAuth();
    const [partnerName, setPartnerName] = React.useState('');
    const [aDemander, setADemander] = React.useState<RequestEntry[]>([]);
    const [demandees, setDemandees] = React.useState<RequestEntry[]>([]);
    const [confirmeesParPrestataire, setConfirmeesParPrestataire] = React.useState<RequestEntry[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [sendingId, setSendingId] = React.useState<string | null>(null);
    const [confirmingId, setConfirmingId] = React.useState<string | null>(null);
    const [cancelingId, setCancelingId] = React.useState<string | null>(null);
    const [error, setError] = React.useState('');
    const [selectedEntry, setSelectedEntry] = React.useState<RequestEntry | null>(null);
    const [emailHistoryFor, setEmailHistoryFor] = React.useState<RequestEntry | null>(null);
    const [statusFilter, setStatusFilter] = React.useState<'all' | StatusValue>('all');

    const load = React.useCallback((query?: string) => {
        if (!id) return;
        setLoading(true);
        Promise.all([moduleApi.getExtrasPartner(id), moduleApi.getExtrasPartnerRequests(id, query)]).then(([p, d]: any[]) => {
            setPartnerName(p?.name || '');
            setADemander(Array.isArray(d?.a_demander) ? d.a_demander : []);
            setDemandees(Array.isArray(d?.demandees) ? d.demandees : []);
            setConfirmeesParPrestataire(Array.isArray(d?.confirmees_par_prestataire) ? d.confirmees_par_prestataire : []);
        }).catch((e: any) => setError(e?.message || 'Erreur chargement')).finally(() => setLoading(false));
    }, [id]);

    React.useEffect(() => { load(); }, [load]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        load(q.trim() || undefined);
    };

    const handleSend = async (r: RequestEntry) => {
        if (!id) return;
        setSendingId(r.request_id);
        setError('');
        try {
            const res: any = await moduleApi.sendExtrasRequest(id, r.request_id);
            if (res?.error) { setError(res.error); return; }
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'envoi de la demande.");
        } finally {
            setSendingId(null);
        }
    };

    const handleConfirmManually = async (r: RequestEntry) => {
        if (!id) return;
        setConfirmingId(r.request_id);
        setError('');
        try {
            await moduleApi.confirmExtrasRequestManually(id, r.request_id);
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la confirmation.');
        } finally {
            setConfirmingId(null);
        }
    };

    // sansConfirmation : la question est posée UNE fois au niveau du groupe, pas une fois par poste.
    const handleCancel = async (r: RequestEntry, sansConfirmation = false) => {
        if (!id) return;
        if (!sansConfirmation && !confirm(`Annuler la demande "${r.type_label}" ?`)) return;
        setCancelingId(r.request_id);
        setError('');
        try {
            const res: any = await moduleApi.cancelExtrasRequest(id, r.request_id);
            if (res?.error) { setError(res.error); return; }
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'annulation.");
        } finally {
            setCancelingId(null);
        }
    };

    const [downloadingRecapId, setDownloadingRecapId] = React.useState<string | null>(null);
    const autoAttachedEventIds = React.useRef<Set<string>>(new Set());

    // Récap PDF des extras VALIDÉS par ce prestataire pour une privatisation — mêmes fiches Prestataire/
    // Client + date & heure que le récap de commande fournisseur, jamais les suggestions de personnes.
    // Demande explicite d'Emeni (2026-08-22). `download=false` = rattachement silencieux en arrière-plan.
    const buildAndAttachRecap = async (eventId: string | null, confirmed: RequestEntry[], download: boolean): Promise<boolean> => {
        if (confirmed.length === 0 || !id) return false;
        const [event, partner] = await Promise.all([
            eventId ? moduleApi.getEvenementielEvent(eventId).catch(() => null) : Promise.resolve(null),
            moduleApi.getExtrasPartner(id).catch(() => null),
        ]);
        const eventLabel = confirmed[0].event_label || 'Privatisation';
        if (!download && eventId) {
            const expectedName = `Extras_confirmes_${eventLabel.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
            const already = Array.isArray((event as any)?.documents) && (event as any).documents.some((d: any) => d.file_name === expectedName);
            if (already) return false;
        }
        // UNE PRIVATISATION SUPPRIMÉE LAISSE DES DEMANDES D'EXTRAS ORPHELINES : `event` vaut alors null.
        // Continuer produisait un POST .../documents voué à un 404. L'erreur était avalée côté navigateur
        // (personne ne voyait rien), mais le SERVEUR la comptait : au bout de 5 requêtes vers des routes
        // inexistantes en 2 minutes, le bannissement automatique coupe l'accès à TOUTE l'application, pas
        // seulement à ce module. Deux lignes orphelines par ouverture suffisaient donc à bannir un client
        // en trois consultations. Constaté en production le 27/08, sur des comptes réels.
        //
        // Un clic MANUEL continue de produire le PDF — l'utilisateur l'a demandé, il doit l'obtenir — mais
        // sans tenter de le rattacher à une privatisation qui n'existe plus.
        // ATTENTION : GET /evenementiel/:id repond 200 avec une COQUILLE {id, note_text, staff,
        // assignments, spaces} meme pour un evenement supprime — il la fabrique a partir de l'identifiant.
        // Tester la nullite de `event` ne suffit donc pas, il faut un champ que seule une vraie ligne porte.
        const privatisationSupprimee = !!eventId && !(event as any)?.start_time;
        if (privatisationSupprimee && !download) return false;
        const result = await generateExtrasConfirmedPdf({
            eventLabel,
            eventDate: confirmed[0].request_date || undefined,
            eventStartTime: (event as any)?.start_time ? String((event as any).start_time).slice(11, 16) : undefined,
            eventEndTime: (event as any)?.end_time ? String((event as any).end_time).slice(11, 16) : undefined,
            numPeople: (event as any)?.num_people || null,
            clientPhone: (event as any)?.phone || undefined,
            clientEmail: (event as any)?.email || undefined,
            partners: [{ name: partnerName, phone: (partner as any)?.phone || undefined, email: (partner as any)?.email || undefined }],
            items: confirmed.map((r) => ({
                type_label: r.type_label, partner_name: partnerName, quantity: r.quantity,
                start_time: r.start_time, end_time: r.end_time, confirmed_comment: r.confirmed_comment,
            })),
            issuerName: user?.company_name || undefined,
            logoUrl: user?.logo_url ? resolveLogoUrl(user.logo_url) : null,
            brandColor: user?.theme_color || undefined,
            download,
        });
        // Rattaché automatiquement aux "Documents" de la privatisation concernée — demande explicite d'Emeni
        // (2026-08-22). Jamais si la privatisation a disparu : il n'y a plus rien où le ranger.
        if (result && eventId && !privatisationSupprimee) {
            await moduleApi.addEvenementielDocument(eventId, { file_name: result.fileName, mime_type: result.mimeType, base64: result.base64 }).catch(() => {});
            return true;
        }
        return !!result;
    };

    // Rattachement automatique silencieux dès qu'un groupe (événement) apparaît ici avec au moins une ligne
    // confirmée — couvre le cas où le prestataire confirme lui-même via son lien email (session différente,
    // le PDF ne peut pas y être généré côté navigateur). Demande explicite d'Emeni (2026-08-22).
    React.useEffect(() => {
        if (confirmeesParPrestataire.length === 0) return;
        const byEvent = new Map<string, RequestEntry[]>();
        confirmeesParPrestataire.forEach((r) => {
            if (!r.event_id || autoAttachedEventIds.current.has(r.event_id)) return;
            if (!byEvent.has(r.event_id)) byEvent.set(r.event_id, []);
            byEvent.get(r.event_id)!.push(r);
        });
        byEvent.forEach((rows, eventId) => {
            autoAttachedEventIds.current.add(eventId);
            buildAndAttachRecap(eventId, rows, false).catch(() => {});
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmeesParPrestataire]);

    const handleDownloadConfirmedRecap = async (rows: RequestEntry[]) => {
        const confirmed = rows.filter((r) => deriveStatus(r) === 'confirmee');
        if (confirmed.length === 0 || !id) return;
        const eventId = confirmed[0].event_id;
        setDownloadingRecapId(eventId || confirmed[0].request_id);
        try {
            await buildAndAttachRecap(eventId, confirmed, true);
        } finally {
            setDownloadingRecapId(null);
        }
    };

    const allRows = React.useMemo(() => [...aDemander, ...demandees, ...confirmeesParPrestataire], [aDemander, demandees, confirmeesParPrestataire]);
    const filteredRows = React.useMemo(() => statusFilter === 'all' ? allRows : allRows.filter((r) => deriveStatus(r) === statusFilter), [allRows, statusFilter]);

    // UNE ligne par privatisation ET par partenaire : le suivi en affichait une par type de poste,
    // soit deux fois le nom du partenaire, deux fois la date et deux boutons pour un seul et meme
    // email. Demande d'Emeni (2026-08-25).
    const groupedRows = React.useMemo(() => groupByPartnerAndEvent(filteredRows), [filteredRows]);

    const chipOptions = [
        { value: 'a_demander', label: 'À demander', count: aDemander.length },
        { value: 'demandee', label: 'Demandée', count: demandees.length },
        { value: 'confirmee', label: 'Confirmée', count: confirmeesParPrestataire.length },
    ];

    // Réutilisée à la fois pour les actions en ligne du tableau et pour le pied de la modale de détail
    // (Emeni : "quand je clique sur une commande extras, il m'affiche toutes les infos + les boutons
    // disponibles") — `afterAction` ferme la modale une fois une action déclenchée depuis celle-ci.
    const handleCancelGroup = async (lot: RequestEntry[]) => {
        if (lot.length > 1) {
            const libelles = lot.map((x) => `${x.type_label} x${x.quantity}`).join(', ');
            if (!confirm(`Annuler les ${lot.length} demandes de cette privatisation ?

${libelles}`)) return;
        }
        for (const item of lot) await handleCancel(item, true);
    };
    const handleConfirmGroup = async (lot: RequestEntry[]) => {
        for (const item of lot) await handleConfirmManually(item);
    };

    // `group` : toutes les lignes de la ligne groupée. Annuler ou confirmer doit les traiter TOUTES,
    // sinon un clic n'en retirerait qu'une et la ligne resterait à l'écran avec le reste.
    const renderRowActions = (r: RequestEntry, afterAction?: () => void, group?: RequestEntry[]) => {
        const lot = group && group.length ? group : [r];
        const status = deriveStatus(r);
        // Le serveur enverra un SEUL email couvrant tout le lot : le bouton doit le dire, sinon deux
        // lignes du meme partenaire affichent deux boutons identiques et on croit a un doublon.
        const batchSize = sameEmailBatch(allRows, r).length;
        return (
            <>
                {status === 'a_demander' && (
                    <>
                        <Button size="sm" onClick={() => { handleSend(r); afterAction?.(); }} disabled={sendingId === r.request_id || cancelingId === r.request_id}>
                            {sendingId === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                            {batchActionLabel('Envoyer', batchSize)}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => { handleCancelGroup(lot); afterAction?.(); }} disabled={sendingId === r.request_id || cancelingId === r.request_id}>
                            {cancelingId === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                        </Button>
                    </>
                )}
                {status === 'demandee' && (
                    <>
                        <Button size="sm" variant="secondary" onClick={() => { handleSend(r); afterAction?.(); }} disabled={sendingId === r.request_id || confirmingId === r.request_id || cancelingId === r.request_id}>
                            {sendingId === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                            {batchActionLabel('Relancer', batchSize)}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => { handleConfirmGroup(lot); afterAction?.(); }} disabled={confirmingId === r.request_id || cancelingId === r.request_id}>
                            {confirmingId === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <BadgeCheck size={13} />}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => { handleCancelGroup(lot); afterAction?.(); }} disabled={confirmingId === r.request_id || cancelingId === r.request_id}>
                            {cancelingId === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                        </Button>
                    </>
                )}
                <Button size="sm" variant="secondary" onClick={() => { setEmailHistoryFor(r); afterAction?.(); }} title="Historique des mails">
                    <Mail size={13} />
                </Button>
            </>
        );
    };

    return (
        <div className="space-y-6">
            <div>
                <button onClick={() => navigate(`/extras-partenaires/${id}`)} className="flex items-center gap-1.5 text-sm text-content-muted hover:text-content font-medium mb-2">
                    <ArrowLeft size={15} /> {partnerName || 'Partenaire'}
                </button>
                <h1 className="text-xl font-bold text-content tracking-tight flex items-center gap-2"><HardHat size={18} /> Demandes d'extras</h1>
            </div>

            <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative flex-1 max-w-sm">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un type, une privatisation..." className="pl-8" />
                </div>
                <Button type="submit" variant="secondary" size="sm">Rechercher</Button>
            </form>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <StatusFilterChips options={chipOptions} value={statusFilter} onChange={(v) => setStatusFilter(v as any)} />

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
            ) : (
                <StatusTrackingTable<GroupedRequest<RequestEntry>>
                    rows={groupedRows}
                    getRowKey={(g) => g.key}
                    onRowClick={(g) => setSelectedEntry(g.lead)}
                    groupBy={(g) => g.lead.event_id ? { key: g.lead.event_id, label: g.lead.event_label, date: g.lead.request_date } : null}
                    // Statut du groupe = le MOINS avancé : c'est ce qu'il reste à faire qui doit sauter aux yeux.
                    getStatus={(g) => STATUS_TONE[[...g.requests].map(deriveStatus)
                        .sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))[0]]}
                    emptyMessage="Aucune demande pour l'instant."
                    renderGroupActions={(groups) => {
                        const rows = groups.flatMap((g) => g.requests);
                        return rows.some((r) => deriveStatus(r) === 'confirmee') ? (
                            <Button size="sm" variant="secondary" onClick={() => handleDownloadConfirmedRecap(rows)}
                                disabled={downloadingRecapId === (rows[0]?.event_id || rows[0]?.request_id)}>
                                {downloadingRecapId === (rows[0]?.event_id || rows[0]?.request_id) ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Récap extras confirmés
                            </Button>
                        ) : null;
                    }}
                    columns={[
                        { key: 'type', header: 'Type', render: (g) => (
                            // Chaque poste porte SA quantité et SON horaire complet : n'afficher que l'heure de
                            // début, ou la masquer quand les horaires se ressemblent, privait le lecteur de
                            // l'essentiel — « on n'a pas toutes les infos » (Emeni, 2026-08-25). Le partenaire
                            // doit savoir combien de personnes, à quelle heure, et jusqu'à quand.
                            <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                                {g.requests.map((r, i) => {
                                    const debut = r.start_time ? String(r.start_time).slice(0, 5) : '';
                                    const fin = r.end_time ? String(r.end_time).slice(0, 5) : '';
                                    return (
                                        <span key={r.request_id} className="whitespace-nowrap">
                                            <span className="font-bold">{r.type_label}</span>
                                            <span className="text-content-muted"> x{r.quantity}</span>
                                            {debut && (
                                                <span className="text-content-muted text-[11px]">
                                                    {' · '}{debut}{fin ? ` → ${fin}` : ''}
                                                </span>
                                            )}
                                            {i < g.requests.length - 1 && <span className="text-content-muted">,</span>}
                                        </span>
                                    );
                                })}
                                {/* Repere d'ecart : le besoin enregistre sur la privatisation depasse ce qui est
                                    reellement commande. Relever un besoin depuis "Modifier" n'envoie rien au
                                    prestataire (seul "Booker" commande, regle d'Emeni) et l'ecart restait invisible :
                                    le 2026-08-25, 4 hotesses necessaires, 2 seulement demandees, aucun signal.
                                    Present a l'identique dans ExtrasModule.tsx : les deux ecrans affichent ce suivi. */}
                                {postesIncomplets(g.requests).map((t) => (
                                    <span key={t.typeId} className="whitespace-nowrap text-[11px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30"
                                        title="Le besoin enregistre sur la privatisation depasse ce qui est commande. Rouvrez la privatisation et cliquez sur Booker pour creer la demande manquante.">
                                        {t.label} : {t.pourvu} sur {t.besoin} demandes
                                    </span>
                                ))}
                            </span>
                        ) },
                        { key: 'date', header: 'Date', render: (g) => g.lead.request_date ? (
                            // Plus d'amplitude globale ici : les horaires exacts sont désormais portés par chaque
                            // poste, la résumer ferait doublon et laissait croire à un horaire unique.
                            <span className="text-content-muted flex items-center gap-1"><CalendarDays size={11} /> {fmtDate(g.lead.request_date)}</span>
                        ) : '—' },
                        { key: 'derniere_action', header: 'Dernière action', render: (g) => {
                            // La plus récente des lignes du groupe : elles partent ensemble, la date la plus
                            // fraîche est celle qui décrit vraiment où en est le dossier.
                            const confirmees = g.requests.filter((r) => r.confirmed_at).map((r) => r.confirmed_at as string).sort();
                            if (confirmees.length === g.requests.length && confirmees.length) {
                                return <span className="text-content-muted">Confirmée le {fmtDate(confirmees[confirmees.length - 1])}</span>;
                            }
                            const envoyees = g.requests.filter((r) => r.requested_at).map((r) => r.requested_at as string).sort();
                            if (envoyees.length) {
                                const relance = g.requests.some((r) => r.email_count > 1);
                                return <span className="text-content-muted">{relance ? 'Relancée' : 'Envoyée'} le {fmtDate(envoyees[envoyees.length - 1])}</span>;
                            }
                            return <span className="text-content-muted">—</span>;
                        } },
                    ]}
                    renderActions={(g) => renderRowActions(g.lead, undefined, g.requests)}
                />
            )}

            {selectedEntry && (
                <RequestDetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)}
                    actions={renderRowActions(selectedEntry, () => setSelectedEntry(null))} />
            )}
            {emailHistoryFor && id && (
                <EmailHistoryModal
                    title={`Historique des mails — ${emailHistoryFor.type_label}`}
                    fetchHistory={() => moduleApi.getExtrasRequestEmailHistory(id, emailHistoryFor.request_id).then((res: any) => Array.isArray(res?.emails) ? res.emails : [])}
                    onClose={() => setEmailHistoryFor(null)}
                />
            )}
        </div>
    );
};
